import AsyncStorage from '@react-native-async-storage/async-storage';

const KEYS = {
  ID_TOKEN: '@eleplan/id_token',
  ACCESS_TOKEN: '@eleplan/access_token',
  REFRESH_TOKEN: '@eleplan/refresh_token',
  REFRESH_CLIENT_ID: '@eleplan/refresh_client_id',
  DEVICE_KEY: '@eleplan/device_key',
  USERNAME: '@eleplan/username',
  EXPIRES_AT: '@eleplan/expires_at',
};

// AsyncStorage operations are asynchronous, but session ownership changes are
// synchronous JavaScript events. Increment this before every token mutation so
// an older refresh can reserve a write/clear only while it still owns the
// session that started it. The counter is intentionally process-local: it
// prevents in-flight work in this app instance from crossing a sign-out or a
// newer login; persisted tokens remain the source of truth across launches.
let sessionGeneration = 0;

export function getSessionGeneration(): number {
  return sessionGeneration;
}

function beginSessionMutation(): number {
  sessionGeneration += 1;
  return sessionGeneration;
}

/** Cognito includes the tracked device key in access-token claims. */
export function getDeviceKeyFromAccessToken(accessToken: string): string {
  try {
    const payloadB64 = accessToken.split('.')[1] || '';
    if (!payloadB64) return '';
    const base64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload?.device_key === 'string' ? payload.device_key : '';
  } catch {
    return '';
  }
}

export interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  refreshClientId: string;
  deviceKey: string;
  username: string;
  expiresAt: number;
}

export interface TokensToStore {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  refreshClientId?: string;
  deviceKey?: string;
  username: string;
  expiresIn: number;
}

async function persistTokens(tokens: TokensToStore): Promise<void> {
  const expiresAt = Date.now() + tokens.expiresIn * 1000;
  const deviceKey = tokens.deviceKey || getDeviceKeyFromAccessToken(tokens.accessToken);
  await AsyncStorage.multiSet([
    [KEYS.ID_TOKEN, tokens.idToken],
    [KEYS.ACCESS_TOKEN, tokens.accessToken],
    [KEYS.REFRESH_TOKEN, tokens.refreshToken],
    [KEYS.REFRESH_CLIENT_ID, tokens.refreshClientId || ''],
    [KEYS.DEVICE_KEY, deviceKey],
    [KEYS.USERNAME, tokens.username],
    [KEYS.EXPIRES_AT, String(expiresAt)],
  ]);
}

export async function storeTokens(tokens: TokensToStore): Promise<void> {
  beginSessionMutation();
  await persistTokens(tokens);
}

/**
 * Persist refreshed credentials only if no sign-out or newer login has begun.
 * The synchronous reservation closes the check/write gap: any later token
 * mutation increments the generation before its first await, queues after this
 * write, and therefore both wins storage order and invalidates this result.
 */
export async function storeTokensIfSessionCurrent(
  tokens: TokensToStore,
  expectedGeneration: number,
): Promise<number | null> {
  if (sessionGeneration !== expectedGeneration) return null;
  const reservation = beginSessionMutation();
  await persistTokens(tokens);
  return sessionGeneration === reservation ? reservation : null;
}

export async function getStoredTokens(): Promise<StoredTokens | null> {
  const readGeneration = sessionGeneration;
  const pairs = await AsyncStorage.multiGet([
    KEYS.ID_TOKEN,
    KEYS.ACCESS_TOKEN,
    KEYS.REFRESH_TOKEN,
    KEYS.REFRESH_CLIENT_ID,
    KEYS.DEVICE_KEY,
    KEYS.USERNAME,
    KEYS.EXPIRES_AT,
  ]);

  const map = Object.fromEntries(pairs.map(([k, v]) => [k, v]));
  const idToken = map[KEYS.ID_TOKEN];
  const accessToken = map[KEYS.ACCESS_TOKEN];
  const refreshToken = map[KEYS.REFRESH_TOKEN];
  const refreshClientId = map[KEYS.REFRESH_CLIENT_ID];
  const storedDeviceKey = map[KEYS.DEVICE_KEY];
  const username = map[KEYS.USERNAME];
  const expiresAt = Number(map[KEYS.EXPIRES_AT] || 0);

  if (!idToken || !accessToken || !refreshToken) return null;

  // Sessions created by older app builds did not persist DEVICE_KEY. Recover it
  // from the access token once so their next refresh uses Cognito's required
  // device-tracking contract without forcing a fresh password sign-in.
  const deviceKey = storedDeviceKey || getDeviceKeyFromAccessToken(accessToken);
  if (!storedDeviceKey && deviceKey && sessionGeneration === readGeneration) {
    await AsyncStorage.setItem(KEYS.DEVICE_KEY, deviceKey);
  }

  return {
    idToken,
    accessToken,
    refreshToken,
    refreshClientId: refreshClientId || '',
    deviceKey,
    username: username || '',
    expiresAt
  };
}

export async function getIdToken(): Promise<string> {
  const token = await AsyncStorage.getItem(KEYS.ID_TOKEN);
  return token || '';
}

export async function getAccessToken(): Promise<string> {
  const token = await AsyncStorage.getItem(KEYS.ACCESS_TOKEN);
  return token || '';
}

export async function isTokenExpired(): Promise<boolean> {
  const expiresAt = await AsyncStorage.getItem(KEYS.EXPIRES_AT);
  if (!expiresAt) return true;
  return Date.now() > Number(expiresAt) - 60_000;
}

export async function clearTokens(): Promise<void> {
  beginSessionMutation();
  await AsyncStorage.multiRemove(Object.values(KEYS));
}

/** Reserve terminal cleanup before it yields to best-effort push teardown. */
export function reserveSessionClear(expectedGeneration: number): number | null {
  if (sessionGeneration !== expectedGeneration) return null;
  return beginSessionMutation();
}

/**
 * Clear only if the cleanup reservation still owns the session. If a new
 * login starts after multiRemove is queued, its later multiSet wins and the
 * changed generation prevents the old caller from clearing Redux state.
 */
export async function clearTokensIfReserved(reservation: number): Promise<boolean> {
  if (sessionGeneration !== reservation) return false;
  await AsyncStorage.multiRemove(Object.values(KEYS));
  return sessionGeneration === reservation;
}
